import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BAND_APPOINTMENTS_QUERY, BAND_CONTACTS_QUERY, BAND_PLANS_QUERY, BAND_PROCEDURES_QUERY } from './graphql'
import type {
  ActionCenterBandAppointmentsQuery,
  ActionCenterBandContactsQuery,
  ActionCenterBandPlansQuery,
  ActionCenterBandProceduresQuery,
} from 'app/generated/gql/graphql'
import { readLocationIds } from './locationParam'
import { contactNameTerms } from 'lib/contactSearch'
import { TREATMENT_POPULATION, TreatmentBandId, bandSearchTerms } from './treatmentBands'
import { FilterJoin, RowGroup, cardMatchesGroups, procedureRows } from './procedureFilter'
import { encodeFilters, type FilterObject } from 'app/filter.utils'
import { useProcedureFilter } from './useProcedureFilter'
import { useScheduleSplit } from './useScheduleSplit'
import type { BookedGroup } from 'app/types/treatment'
import { BOOKED_GROUPS, hiddenSearchTerms } from 'lib/treatmentBooked'
import {
  RawAppointment,
  RawPlan,
  RawProcedure,
  TreatmentCard,
  TreatmentContact,
  buildCard,
  byPatientId,
  forContact,
} from 'lib/treatmentCards'
import { gqlQuery } from 'app/lib/graphqlClient'
import { logError } from 'lib/logging'

/**
 * A page of cases, small enough that the biggest ones are on screen fast.
 *
 * The oldest band holds well over two thousand people. Loading them all was
 * fine for a nine-card sample and impossible for the real thing, so the board
 * takes the top of the band and asks for more only when someone works through
 * it.
 */
const PAGE_SIZE = 24

/**
 * How far back the board reads a patient's appointments.
 *
 * Long enough to hold every booking that fell through recently and everything
 * still to come, short enough that a page of two dozen patients is a couple of
 * hundred records rather than a couple of thousand.
 */
const HISTORY_MONTHS = 18

type Page = {
  contacts: TreatmentContact[]
  procedures: RawProcedure[]
  plans: RawPlan[]
  appointments: RawAppointment[]
}

/** What a page of contacts needs read beside it to build their cards. */
async function readRecords(contacts: TreatmentContact[], locationTerm?: string): Promise<Omit<Page, `contacts`>> {
  const patientIds = contacts.flatMap((contact) => contact.patientIds ?? [])
  if (patientIds.length === 0) return { procedures: [], plans: [], appointments: [] }
  const patients = `patientId:(${patientIds.map((id) => `"${id}"`).join(` OR `)})`
  const since = moment().subtract(HISTORY_MONTHS, `months`).format(`YYYY-MM-DD`)
  // Three requests for the whole page, fired together. Waiting on each in
  // turn would treble the time a card takes to arrive for no reason:
  // none of them needs anything from the others.
  const [procedureResult, planResult, appointmentResult] = await Promise.all([
    gqlQuery<ActionCenterBandProceduresQuery>(BAND_PROCEDURES_QUERY, {
      search: `${patients} AND state:"PENDING"`,
      pagination: { limit: 1000 },
      sort: { field: `feeAmount`, direction: `DESC` },
    }),
    gqlQuery<ActionCenterBandPlansQuery>(BAND_PLANS_QUERY, {
      search: patients,
      pagination: { limit: 1000 },
    }),
    gqlQuery<ActionCenterBandAppointmentsQuery>(BAND_APPOINTMENTS_QUERY, {
      search: [patients, `date:[${since} TO *]`, ...(locationTerm ? [locationTerm] : [])].join(` AND `),
      pagination: { limit: 1000 },
      sort: { field: `date`, direction: `DESC` },
    }),
  ])
  // Procedures are the one join the board cannot shrug off: a card with
  // none is filtered out below, so a failed procedures fetch would render
  // as an empty board claiming there are no cases, rather than as a
  // fault. It fails the page instead. Plans and appointments only
  // decorate, so their failures leave the card thinner, not wrong.
  if (procedureResult.error) throw procedureResult.error
  for (const [label, result] of [
    [`plans`, planResult],
    [`appointments`, appointmentResult],
  ] as const) {
    if (result.error) logError(`Action Center: treatment ${label}`, result.error)
  }
  return {
    procedures: procedureResult.data?.procedures?.hits ?? [],
    plans: planResult.data?.treatmentPlans?.hits ?? [],
    appointments: appointmentResult.data?.appointments?.hits ?? [],
  }
}

/**
 * One patient's case, wherever it sits: a deep link can name somebody past the pages a board
 * has read, or outside its band and filters altogether. Undefined when there is no case to draw.
 */
export async function readCase(contactId: string): Promise<TreatmentCard | undefined> {
  const contactResult = await gqlQuery<ActionCenterBandContactsQuery>(BAND_CONTACTS_QUERY, {
    search: `id:"${contactId}"`,
    pagination: { limit: 1 },
  })
  if (contactResult.error) throw contactResult.error
  const contact = contactResult.data?.contacts?.hits?.[0]
  if (!contact) return undefined
  const { procedures, plans, appointments } = await readRecords([contact])
  const card = buildCard(contact, procedures, plans, appointments)
  return card.procedures.length > 0 ? card : undefined
}

/**
 * The cases in a band, from the practice's own records.
 *
 * Two queries per page rather than one per card: the contacts come back with
 * the money on them, then a single appointments query covers every patient on
 * the page at once. Twenty-four cards cost two round trips, not twenty-five.
 */
/**
 * `sort` and `direction` are spelled as literals rather than imported from
 * `useTreatmentBoard`: that module imports this one, and naming its types here
 * would close the cycle.
 */
export type BandCardsOptions = {
  band?: TreatmentBandId
  hidden?: BookedGroup[]
  sort?: `presented` | `value`
  /**
   * Which end of that sort comes first. The board only ever wants the biggest
   * and the freshest, so `DESC` is the default; the table's column headers are
   * the one caller that asks for the other end.
   */
  direction?: `ASC` | `DESC`
  /**
   * Replaces the band's own search entirely, for a list that is not a band:
   * the exits, whose people are found by pipeline membership rather than by
   * having treatment outstanding. It deliberately drops the location
   * drill-down and the search box with it, because those narrow what you are
   * working, and this is a list of what has already left.
   */
  searchOverride?: string
  /**
   * A saved treatment-plan list's rule: the band is cut by it first, and the Filters
   * builder's own rows and location only narrow further (see `RowGroup`).
   */
  rule?: { filters: FilterObject[]; join: FilterJoin }
  /** Search terms that keep people off the band, such as a list's exit statuses. Not applied to `searchOverride`. */
  excludeTerms?: string[]
  /** Replaces the default value term (`TREATMENT_POPULATION`) in the band and in the all-bands read. */
  population?: string
  /**
   * Keeps reading pages on its own, one after another, until this many people are in hand or the
   * band runs out. A board pages one ranking across every column, so a part-read band leaves each
   * column part-filled and its count and value short; past the ceiling "Load more" takes over.
   */
  fillUpTo?: number
}

const NOTHING_HIDDEN: BookedGroup[] = []
const NOTHING_EXCLUDED: string[] = []

export function useBandCards({
  band,
  hidden = NOTHING_HIDDEN,
  sort = `presented`,
  direction = `DESC`,
  searchOverride,
  rule,
  excludeTerms = NOTHING_EXCLUDED,
  population,
  fillUpTo,
}: BandCardsOptions = {}) {
  const [searchParams] = useSearchParams()
  const filters = searchParams.get(`filters`)
  // The rule is read through the same parsers as the URL, so a list and the builder can
  // never disagree on what a row or a location means.
  const ruleParam = useMemo(() => (rule ? encodeFilters(rule.filters) : null), [rule])
  const locationTerm = useMemo(() => {
    // The list's locations and the drill-down's must both hold: one term each, ANDed.
    const terms = [readLocationIds(ruleParam), readLocationIds(filters)]
      .filter((ids) => ids.length > 0)
      .map((ids) => `locationId:(${ids.map((id) => `"${id}"`).join(` OR `)})`)
    return terms.length ? terms.join(` AND `) : undefined
  }, [filters, ruleParam])
  // The Filters builder writes its rows into the same param as the location;
  // these are the ones about the treatment itself. See `procedureFilter.ts`.
  const rows = useMemo(() => procedureRows(filters), [filters])
  // The builder writes `join` only with two or more rows; absent means AND.
  const join: FilterJoin = searchParams.get(`join`) === `or` ? `or` : `and`
  const groups = useMemo<RowGroup[]>(
    () =>
      [
        { rows: procedureRows(ruleParam), join: rule?.join ?? `and` },
        { rows, join },
      ].filter((group) => group.rows.length > 0),
    [ruleParam, rule?.join, rows, join],
  )

  const [pages, setPages] = useState<Page[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [wanted, setWanted] = useState(1)
  // Every fetch carries a token, so a page arriving after the band changed is
  // dropped rather than stitched onto a board it does not belong to.
  const generation = useRef(0)

  // What was typed in the board's search box, the same `query` param the
  // contacts and pipelines pages read. Searched at the index rather than
  // filtered here: a band holds thousands and the board reads 24 at a time,
  // so filtering the page in hand would answer "nobody" for a patient who is
  // plainly in the band. `contactNameTerms` escapes it, because this string
  // reaches ES `query_string` as written.
  const query = searchParams.get(`query`) ?? ``
  // `null`: every group is hidden, so the board is empty by choice.
  const search = useMemo<string | null>(() => {
    if (searchOverride) return searchOverride
    if (BOOKED_GROUPS.every((group) => hidden.includes(group.id))) return null
    const terms = band ? bandSearchTerms(band, undefined, population) : [population ?? TREATMENT_POPULATION]
    terms.push(...hiddenSearchTerms(hidden))
    terms.push(...excludeTerms)
    if (locationTerm) terms.push(locationTerm)
    terms.push(...contactNameTerms(query))
    return terms.join(` `)
  }, [band, hidden, excludeTerms, population, locationTerm, query, searchOverride])

  // Paging and display sort by the same field, so "load more" keeps adding to
  // the bottom of the order on screen instead of shuffling what is already read.
  const sortField = sort === `value` ? `unbookedTreatmentValue` : `lastTreatmentPlanDate`

  // Two cuts the index cannot make, so the board pages a list it holds: one
  // booked group hidden and the other kept, and any row about what the
  // treatment is. With both on, the procedure filter cuts the split's list.
  const splitsBooked = hidden.includes(`treatment`) !== hidden.includes(`hygiene`)
  const splitLocal = splitsBooked && !searchOverride && search !== null
  const filtered = groups.length > 0 && !searchOverride && search !== null
  const split = useScheduleSplit(splitLocal, { search: search ?? ``, sortField, direction, locationTerm, hidden })
  const filteredRead = useProcedureFilter(filtered, {
    groups,
    search: search ?? ``,
    sortField,
    direction,
    locationTerm,
    cohort: splitLocal ? split : undefined,
  })
  const local = splitLocal || filtered
  const localRead = filtered ? filteredRead : split

  useEffect(() => {
    generation.current += 1
    setPages([])
    setWanted(1)
    setError(undefined)
    setLoading(true)
  }, [search, sortField, direction, localRead.contacts])

  useEffect(() => {
    const token = ++generation.current
    let cancelled = false
    setLoading(true)
    if (search === null) {
      setTotal(0)
      setLoading(false)
      return
    }
    if (local && !localRead.ready) return

    const run = async () => {
      const page = wanted - 1
      let contacts: TreatmentContact[]
      let total: number
      if (local) {
        contacts = localRead.contacts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
        total = localRead.contacts.length
      } else {
        const contactResult = await gqlQuery<ActionCenterBandContactsQuery>(BAND_CONTACTS_QUERY, {
          search,
          pagination: { limit: PAGE_SIZE, page },
          sort: { field: sortField, direction },
        })
        if (contactResult.error) throw contactResult.error
        contacts = contactResult.data?.contacts?.hits ?? []
        total = contactResult.data?.contacts?.total ?? 0
      }
      if (cancelled || token !== generation.current) return

      const records = await readRecords(contacts, locationTerm)
      if (cancelled || token !== generation.current) return

      setTotal(total)
      setPages((current) => {
        const next = [...current]
        next[page] = { contacts, ...records }
        return next
      })
    }

    run()
      .catch((err) => {
        if (cancelled || token !== generation.current) return
        logError(`Action Center: treatment board`, err)
        setError(`These cases could not be loaded.`)
      })
      .finally(() => {
        if (!cancelled && token === generation.current) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [search, sortField, direction, wanted, locationTerm, local, localRead])

  const cards = useMemo<TreatmentCard[]>(() => {
    const now = moment()
    return pages.filter(Boolean).flatMap((page) => {
      const procedures = byPatientId(page.procedures)
      const plans = byPatientId(page.plans)
      const appointments = byPatientId(page.appointments)
      return (
        page.contacts
          .map((contact) =>
            buildCard(
              contact,
              forContact(contact, procedures),
              forContact(contact, plans),
              forContact(contact, appointments),
              now,
            ),
          )
          // The band's search is the rollup, and the rollup counts off-plan
          // hygiene: two contacts on this practice read $49 for a promotional
          // cleaning. Now that a card only classifies plan work, those arrive
          // with nothing on them, and a card with no treatment and no money is
          // an empty column entry telling somebody to make a call about
          // nothing.
          .filter((card) => card.procedures.length > 0)
          // The index matched the record; the card draws only plan work with
          // a fee. A card kept for a line it does not draw would show no
          // reason to be there, so the drawn lines are asked the same rows.
          .filter((card) => cardMatchesGroups(card, groups))
      )
    })
  }, [pages, groups])

  /**
   * Asks for the next page, and only the next page. The guard makes this safe
   * to call from a scroll handler, which fires far faster than a fetch lands:
   * without it a single flick at the bottom of the table would queue page 3
   * before page 2 existed, and `wanted` would run away from what is on screen.
   */
  const loadMore = useCallback(() => setWanted((current) => (pages[current - 1] ? current + 1 : current)), [pages])
  const reload = useCallback(() => {
    setPages([])
    setWanted(1)
    setError(undefined)
  }, [])

  // Against what the server has actually sent, not what survived the card
  // filter: some contacts legitimately build no card, so comparing rendered
  // cards to the server total would offer "Load more" forever.
  const fetched = pages.filter(Boolean).reduce((count, page) => count + page.contacts.length, 0)

  // A short page ends the fill even when `total` says there is more: the index can count a few
  // people it no longer returns, and asking again would walk off the end of the band forever.
  const lastPage = pages[wanted - 1]
  const filling = Boolean(fillUpTo) && !error && fetched < total && fetched < (fillUpTo ?? 0)
  useEffect(() => {
    if (filling && lastPage && lastPage.contacts.length === PAGE_SIZE) setWanted((current) => current + 1)
  }, [filling, lastPage])

  return {
    cards,
    total,
    loading,
    error: error ?? localRead.error,
    loadMore,
    reload,
    hasMore: fetched < total,
  }
}
